'use client';

import React, {
  type ChangeEvent,
  type ChangeEventHandler,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type FC,
  forwardRef,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cva } from 'class-variance-authority';
import { type LucideIcon, Send, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useComposition } from '@/hooks/use-composition';
import AutoGrowingTextArea, {
  type AutoGrowingTextAreaProps,
} from '@/components/ui/auto-growing-textarea';
import { Button, type ButtonProps } from '@/components/ui/button';

interface ChatCommandProps {
  onClick: ButtonProps['onClick'];
  children: ReactElement<LucideIcon>;
}
const ChatCommand: FC<ChatCommandProps> = ({ onClick, children }) => (
  <Button
    variant={'ghost'}
    onClick={onClick}>
    <Button.Icon icon>{children}</Button.Icon>
  </Button>
);
ChatCommand.displayName = 'ChatCommand';

interface ChatActionProps {
  onClick: ButtonProps['onClick'];
  children: ReactElement<LucideIcon>;
}
const ChatAction: FC<ChatActionProps> = ({ onClick, children }) => (
  <Button
    variant={'ghost'}
    onClick={onClick}>
    <Button.Icon icon>{children}</Button.Icon>
  </Button>
);
ChatAction.displayName = 'ChatAction';

interface ChatAttachmentProps {
  file: File;
  onDelete?: (file: File) => void;
}
const ChatAttachment: FC<ChatAttachmentProps> = ({ file, onDelete }) => {
  const [preview, setPreview] = useState<string | null>(null);

  const handleClick = useCallback(() => {
    if (typeof onDelete === 'function') {
      onDelete(file);
    }
  }, [onDelete, file]);

  useEffect(() => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          setPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  }, [file]);

  return (
    <div className='relative size-12 rounded border'>
      <span className='absolute -right-1/2 -top-1/2 z-10 -translate-x-1/2 translate-y-1/2'>
        <Button
          variant={'outline'}
          className='h-6 w-6 p-0'
          onClick={handleClick}>
          <Button.Icon icon>
            <X className='h-4 w-4' />
          </Button.Icon>
        </Button>
      </span>
      {preview ? (
        <img
          className='h-full max-w-full'
          src={preview}
          alt={file.name}
          style={{ objectFit: 'cover' }}
        />
      ) : (
        <div className='rounded bg-gray-100 p-4'>
          <p className='text-xs font-medium'>{file.name.split('.').at(-1)}</p>
        </div>
      )}
    </div>
  );
};
ChatAttachment.displayName = 'ChatAttachment';

interface ChatFileInputProps {
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
}
const ChatFileInput = forwardRef<HTMLInputElement, ChatFileInputProps>(
  ({ onFileChange }, ref) => (
    <input
      ref={ref}
      type='file'
      onChange={onFileChange}
      aria-label='File upload'
      className='hidden'
    />
  )
);
ChatFileInput.displayName = 'ChatFileInput';

const chatInputVariants = cva(
  'bg-white border rounded-xl p-2 gap-2 flex flex-col',
  {
    variants: {
      disabled: {
        true: 'opacity-50 pointer-events-none cursor-not-allowed',
      },
      attachments: {
        true: 'pt-6',
      },
    },
    defaultVariants: {
      disabled: false,
      attachments: false,
    },
  }
);

interface ChatInputProps extends Omit<AutoGrowingTextAreaProps, 'onChange'> {
  onChange?: (value: string) => void;
  onFileChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  children?: ReactNode;
}
const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  ({ children, onChange, onFileChange, ...props }) => {
    const [internalValue, setInternalValue] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [_, commands, attachments] = useComposition(
      children,
      ChatCommand.displayName,
      ChatAttachment.displayName
    );

    const handleDragOver = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
      },
      []
    );

    const handleDrop = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const _files = event.dataTransfer.files;

        if (fileInputRef.current) {
          fileInputRef.current.files = _files;
          if (typeof onFileChange === 'function') {
            onFileChange({
              target: { files: _files },
            } as ChangeEvent<HTMLInputElement>);
          }
        }
      },
      [onFileChange]
    );

    const handleTextAreaChange: ChangeEventHandler<HTMLTextAreaElement> =
      useCallback((event) => {
        setInternalValue(event.target.value);
      }, []);

    const handleSubmit = useCallback(() => {
      if (internalValue.trim() && typeof onChange === 'function') {
        onChange(internalValue);
        setInternalValue('');
      }
    }, [onChange, internalValue]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          handleSubmit();
        }
      },
      [handleSubmit]
    );

    return (
      <div
        className={cn(
          chatInputVariants({
            disabled: props.disabled,
            attachments: (attachments ?? []).length > 0,
          })
        )}
        onDragOver={handleDragOver}
        onDrop={handleDrop}>
        {typeof onFileChange === 'function' && (
          <ChatFileInput
            onFileChange={onFileChange}
            ref={fileInputRef}
          />
        )}
        {Array.isArray(attachments) && attachments.length > 0 ? (
          <div className='flex gap-4'>{attachments}</div>
        ) : null}
        <AutoGrowingTextArea
          {...props}
          value={internalValue}
          onChange={handleTextAreaChange}
          onKeyDown={handleKeyDown}
          className='border-none focus-visible:ring-transparent'
        />
        <div className='flex flex-row justify-between'>
          <div className='flex gap-2'>{commands}</div>
          <div className='flex gap-2'>
            <ChatAction onClick={handleSubmit}>
              <Send />
            </ChatAction>
          </div>
        </div>
      </div>
    );
  }
);
ChatInput.displayName = 'ChatInput';

interface ChatInputComposition {
  Command: typeof ChatCommand;
  Attachment: typeof ChatAttachment;
}

const Root = forwardRef<
  ElementRef<typeof ChatInput>,
  ComponentPropsWithoutRef<typeof ChatInput>
>((props, ref) => (
  <ChatInput
    {...props}
    ref={ref}
  />
));
Root.displayName = 'ChatInput';

const RootWithComposition: typeof Root & ChatInputComposition = Object.assign(
  Root,
  {
    Command: ChatCommand,
    Attachment: ChatAttachment,
  }
);

export { RootWithComposition as ChatInput };
